import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Smartphone } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { startGoogle } from "@/pages/AuthCallback";
import { mobileInput, mobileProps, MobileHint } from "@/lib/india";
import { OtpBox } from "@/components/OtpBox";

import { GoogleIcon } from "@/components/GoogleIcon";
export { GoogleIcon };

const Field = ({ testid, ...p }) => <Input data-testid={testid} className="h-14 rounded-2xl bg-white text-base" {...p} />;

export const PasswordField = ({ value, onChange, placeholder, testid, autoComplete }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Field type={show ? "text" : "password"} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete} testid={testid} />
      <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-4 text-[#7B968B]" data-testid={`${testid}-toggle`} aria-label="show password">
        {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
      </button>
    </div>
  );
};

const Submit = ({ busy, children, testid }) => {
  const { t } = useApp();
  return (
    <button disabled={busy} data-testid={testid}
      className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-[#0D3B2B] text-base font-bold text-white transition-transform active:scale-[.97] disabled:opacity-60">
      {busy ? t("Please wait…") : children}
    </button>
  );
};

const LinkBtn = ({ children, ...p }) => <button type="button" className="font-semibold text-[#2E9E6B]" {...p}>{children}</button>;
const digits = (v) => v.replace(/\D/g, "").slice(-10);

const UsernameLogin = ({ call, busy, back }) => {
  const { t } = useApp();
  const [f, setF] = useState({ identifier: "", password: "" });
  const submit = (e) => {
    e.preventDefault();
    if (!f.identifier.trim() || !f.password) return toast.error(t("Please enter your username and password."));
    call("/auth/login", { identifier: f.identifier.trim(), password: f.password });
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="username-login-form">
      <p className="text-sm text-[#4A6358]">{t("Only if you created a username in your profile.")}</p>
      <Field placeholder={t("Username")} value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} autoComplete="username" autoCapitalize="none" testid="login-identifier-input" />
      <PasswordField placeholder={t("Password")} value={f.password} onChange={(v) => setF({ ...f, password: v })} autoComplete="current-password" testid="login-password-input" />
      <Submit busy={busy} testid="login-submit-btn">{t("Log in")}</Submit>
      <div className="text-center text-sm"><LinkBtn onClick={back} data-testid="username-login-back-link">{t("Back to mobile login")}</LinkBtn></div>
    </form>
  );
};

export const LoginPanel = ({ onDone, beforeGoogle, afterPath = "/", defaultMobile = "" }) => {
  const { t, setUser, meta } = useApp();
  const [mobile, setMobile] = useState(defaultMobile);
  const [step, setStep] = useState("mobile");
  const [busy, setBusy] = useState(false);
  const gid = meta?.google_client_id;
  const google = () => { beforeGoogle?.(); localStorage.removeItem("ks_google_for"); localStorage.setItem("ks_after_login", afterPath); startGoogle(gid); };

  const call = async (url, body) => {
    setBusy(true);
    try { const { data } = await api.post(url, body); setUser(data); onDone?.(data); return true; }
    catch (err) { toast.error(errMsg(err)); return false; }
    finally { setBusy(false); }
  };
  const start = (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(digits(mobile))) return toast.error(t("Please enter a 10-digit mobile number."));
    setStep("login");
  };
  const toSignup = () => { toast.info(t("New to Kindsaver? Let's create your account with this number.")); setStep("signup"); };
  const onOtp = async (otp_token) => {
    const ok = await call(step === "signup" ? "/auth/register" : "/auth/otp-login", step === "signup" ? { mobile: digits(mobile), otp_token } : { otp_token });
    if (!ok) setStep("mobile");
  };

  if (step === "login" || step === "signup") return (
    <div className="space-y-3" data-testid="login-panel">
      {step === "signup" && <p className="rounded-2xl bg-[#E7F6EE] p-3 text-sm font-semibold text-[#1F7A50]" data-testid="signup-notice">{t("No account with this number yet. Verify it to create your Kindsaver account.")}</p>}
      <OtpBox key={step} mobile={digits(mobile)} purpose={step} who="customer" t={t} onVerified={onOtp} onNotFound={toSignup} onBack={() => setStep("mobile")} onGoogle={gid ? google : undefined} />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="login-panel">
      {gid && <>
        <button type="button" onClick={google} data-testid="google-login-btn"
          className="flex h-14 w-full items-center justify-center gap-3 rounded-2xl border-2 border-[#D2E0D8] bg-white text-base font-bold text-[#0D3B2B] shadow-sm transition-transform active:scale-[.97]">
          <GoogleIcon /> {t("Continue with Google")}
        </button>
        <div className="flex items-center gap-3 text-xs font-semibold uppercase text-[#7B968B]"><span className="h-px flex-1 bg-[#D2E0D8]" />{t("or")}<span className="h-px flex-1 bg-[#D2E0D8]" /></div>
      </>}
      {step === "username" ? <UsernameLogin call={call} busy={busy} back={() => setStep("mobile")} /> : (
        <form onSubmit={start} className="space-y-3" data-testid="mobile-login-form">
          <Field {...mobileProps} placeholder={t("Mobile number")} value={mobile} onChange={(e) => setMobile(mobileInput(e.target.value))} testid="login-mobile-input" />
          <MobileHint value={mobile} />
          <Submit busy={busy} testid="login-mobile-continue-btn"><Smartphone className="h-5 w-5" />{t("Continue with mobile number")}</Submit>
          <p className="text-center text-xs text-[#7B968B]">{t("We'll send a 4-digit SMS code. New here? Your account is created after the code is verified.")}</p>
          <div className="text-center text-xs"><LinkBtn onClick={() => setStep("username")} data-testid="go-username-login-link">{t("Created a username? Log in with it")}</LinkBtn></div>
        </form>
      )}
    </div>
  );
};
